import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Platform, ScrollView } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { B2, Icon, OLN, SH3, Stack, colors } from '@zet/design-system'
import { Chip } from '@zet/design-system/chip'
import Pressable from '@zet/design-system/pressable'
import { sendAnalyticsData } from '@zet/integration-flow/utils/analyticsEvents'
import _ from 'lodash'
import { useUnsafeVariable } from 'nativewind'

import DropDownFilterBottomSheet, {
  DropDownFilterState,
} from '../../components/filterBottomSheet/dropDownFilterBottomSheet'
import SubFiltersBottomSheet, {
  SubFilterData,
} from '../../components/filterBottomSheet/subFiltersBottomSheet'
import useCategoryFilters, {
  CategoryFilterData,
} from '../../hooks/useCategoryFilters'
import useFilterStore from '../../hooks/useFilterStore'
import useVariantListingStore from '../../hooks/useVariantListingStore'

const FILTER_BAR_ICONS = {
  sort: {
    primary: 'Sorting',
    secondary: 'ArrowDown',
  },
  FILTERS: {
    primary: 'Preferences',
    secondary: 'ArrowDown',
  },
}

type Filter = {
  key?: string
  type: 'DROPDOWN' | 'SUB_FILTER' | 'QUICK_FILTER'
  displayText?: string
  values?:
    | {
        key: string
        displayText: string
        isMultiSelect: boolean
        values?: { displayText: string; key: string }[]
      }[]
}

type FilterTypes = 'DROPDOWN' | 'SUB_FILTER' | 'QUICK_FILTER'

type CumulativeSelectedFilters = {
  [key in FilterTypes]: DropDownFilterState | SubFilterData
}

type FilterBarIconsType = keyof typeof FILTER_BAR_ICONS

type FilterBarProps = {
  categoryId: number
  customerId?: number
  onChange?: (state: CumulativeSelectedFilters) => void
  isBMSFlow: boolean
  isJfsFlow: boolean
}

const Counter = ({ count }: { count: number }) => {
  const [colorPrimary200] = useUnsafeVariable('--color-primary-200')

  return (
    <Stack
      style={{
        backgroundColor: colorPrimary200,
      }}
      className="h-[16px] w-[16px] items-center justify-center rounded-full"
    >
      <OLN className="leading-[16px] text-white" style={{ lineHeight: 16 }}>
        {count}
      </OLN>
    </Stack>
  )
}

const isWeb = Platform.OS === 'web'

const FilterBar = ({
  categoryId,
  customerId,
  isBMSFlow,
  isJfsFlow,
}: FilterBarProps) => {
  const { data: filters } = useCategoryFilters({
    pageType: customerId
      ? 'RECOMMENDATION_PAGE'
      : 'BRAND_PRODUCT_CATEGORY_PAGE',
    categoryId,
    customerId,
  })
  const [colorPrimary10] = useUnsafeVariable('--color-primary-10')
  const [colorPrimary100] = useUnsafeVariable('--color-primary-100')

  const { subFilterRef } = useVariantListingStore((state) => ({
    subFilterRef: state?.subFilterRef,
  }))

  const {
    subFilters,
    sort,
    quickFilter,
    setQuickFilter,
    clearAllFilters,
    setSubFilters,
    filterValues,
  } = useFilterStore((state) => ({
    quickFilter: state?.quickFilter,
    setQuickFilter: state?.setQuickFilter,
    sort: state?.sort,
    subFilters: state?.subFilters,
    setSubFilters: state?.setSubFilter,
    clearAllFilters: state?.clearAllFilters,
    filterValues: state?.filterValues,
  }))

  const dropDownFilterBSRef = useRef(null)
  const [isVisibleClearAll, setIsVisibleClearAll] = useState(false)

  const navigation = useNavigation()

  const { subFilterData, dropDownFilterData } = useMemo(
    () => ({
      subFilterData: filters?.find((_filter) => _filter?.type === 'SUB_FILTER'),
      dropDownFilterData: filters?.find(
        (_filter) => _filter?.type === 'DROPDOWN'
      ),
    }),
    [filters]
  )

  const [selectedSubFilters, setSelectedSubFilters] = useFilterStore(
    (state) => [state?.subFilters, state?.setSubFilter]
  )

  const onSubFilterSubmit = (selectedFilters: SubFilterData) => {
    if (isJfsFlow && !_.isEmpty(filterValues)) {
      sendAnalyticsData({
        screen: 'FILTERSCREEN_SCREEN_CTA',
        type: 'click',
        optional_data: filterValues,
      })
    }
    subFilterRef?.current?.hide()
    if (selectedSubFilters === selectedFilters) {
      return
    }
    setSelectedSubFilters(selectedFilters)
  }

  const onQuickFilterSelect = (filter: Filter) => {
    const selectedQuickFilters = quickFilter || []
    const isSelected =
      Array.isArray(selectedQuickFilters) &&
      !!selectedQuickFilters?.includes(filter?.key || '')

    let updatedFilterValues

    if (isSelected) {
      // Remove the selected filter(s) based on isMultiSelect flag
      updatedFilterValues = selectedQuickFilters?.filter(
        (_filterKey) => _filterKey !== filter?.key
      )
    } else {
      // Add the selected sub-filter(s) based on isMultiSelect flag
      updatedFilterValues = [...selectedQuickFilters, filter?.key]
    }

    setQuickFilter(
      updatedFilterValues,
      !isSelected && filter.key === 'MOST_POPULAR'
    )
  }

  const onPress = (filter: CategoryFilterData[number]) => {
    if (filter?.key) {
      publishEvent('click_action', {
        item_name: `${filter?.key?.toLowerCase()}_new_${
          Platform.OS === 'web'
            ? 'recommendation_web'
            : customerId
            ? 'recommendation_app'
            : 'brand_list'
        }`,
        product: categoryId,
      })
    }
    if (filter?.type === 'DROPDOWN') {
      if (isJfsFlow) {
        sendAnalyticsData({
          screen: 'SORTSCREEN_SCREEN_LOAD',
          type: 'screen',
        })
      }
      dropDownFilterBSRef.current?.show()
    } else if (filter?.type === 'SUB_FILTER') {
      if (isJfsFlow) {
        sendAnalyticsData({
          screen: 'FILTERSCREEN_SCREEN_LOAD',
          type: 'screen',
        })
      }
      subFilterRef.current?.show()
    } else if (['QUICK_FILTER', 'quickFilter'].includes(filter?.type)) {
      if (isJfsFlow) {
        sendAnalyticsData({
          screen: 'FILTERSCREEN_SCREEN_CTA',
          type: 'click',
          optional_data: {
            [filter?.type]: filter?.key,
          },
        })
      }

      onQuickFilterSelect(filter)
    }
  }

  const resetSubFilters = () => {
    if (isJfsFlow) {
      sendAnalyticsData({
        screen: 'FILTERSCREEN_SCREEN_CTA',
        type: 'click',
        optional_data: {
          type: 'reset',
        },
      })
    }
    subFilterRef?.current?.resetLocalState()
    setSubFilters({})
    subFilterRef?.current?.hide()
  }

  const getFilterState = (filter: Filter) => {
    if (filter?.type === 'SUB_FILTER') {
      let filterCount = 0
      const filterObject = subFilters || {}
      Object.keys(filterObject)?.map((subFilterKey) => {
        const subFilterValue = filterObject[subFilterKey]
        return Array.isArray(subFilterValue)
          ? (filterCount += subFilterValue?.length)
          : filterCount++
      })

      return {
        isSelected: filterCount > 0,
        filterBadgeCount: filterCount,
      }
    } else if (filter?.type === 'DROPDOWN') {
      const filterValue = sort
      if (filterValue) {
        return {
          isSelected: true,
          filterBadgeCount: 0,
        }
      }
    } else if (['QUICK_FILTER', 'quickFilter'].includes(filter?.type)) {
      const filterArray = quickFilter || []
      if (filterArray?.includes(filter?.key)) {
        return {
          isSelected: true,
          filterBadgeCount: 0,
        }
      }
    }

    return {
      isSelected: false,
      filterBadgeCount: 0,
    }
  }

  // To figure out if a filter is active
  useEffect(() => {
    const subFilterKeys = Object.keys(subFilters)

    if (quickFilter?.length > 0 || sort?.length > 0) {
      setIsVisibleClearAll(true)
    } else if (subFilterKeys?.length > 0) {
      for (let index = 0; index < subFilterKeys?.length; index++) {
        if (subFilters?.[subFilterKeys?.[index]]?.length > 0) {
          setIsVisibleClearAll(true)
          break
        }
      }
    } else {
      setIsVisibleClearAll(false)
    }
  }, [sort?.length, quickFilter?.length, subFilters])
  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      try {
        if (isVisibleClearAll) {
          clearAllFilters({
            maintainCategoryId: true,
            resetInitialCategory: false,
          })
          e?.preventDefault()
          if (isWeb) {
            window.history.forward()
          }
        }
      } catch (e) {}
    })
    return unsubscribe
  }, [navigation, isVisibleClearAll])


  const isFilterApplied = useMemo(() => {
    return  sort?.length > 0 || Object.keys(subFilters).length > 0 || quickFilter?.length > 0
  }, [sort, subFilters, quickFilter])

  if (!(Number(filters?.length) > 1)) {
    return
  }

  return (
    <Stack>
      <ScrollView
        horizontal
        scrollEnabled={Number(filters?.length) > 2}
        showsHorizontalScrollIndicator={false}
        alwaysBounceHorizontal={false}
        className={`py-4/2 bg-white px-3 pr-4 ${
          isJfsFlow ? 'border-b border-[#E1E2E5]' : ''
        }`}
        contentContainerStyle={{
          columnGap: 12,
          ...(!isWeb && { paddingRight: 28 }),
        }}
      >
        {isVisibleClearAll && (
          <Pressable
            onPress={() => {
              publishEvent('click_action', {
                item_nam: `remove_filter_${
                  Platform.OS === 'web'
                    ? 'recommendation_web'
                    : customerId
                    ? 'recommendation_app'
                    : 'brand_list'
                }`,
              })
              subFilterRef?.current?.resetLocalState()
              clearAllFilters({
                maintainCategoryId: true,
                resetInitialCategory: false,
              })
              if (isJfsFlow) {
                sendAnalyticsData({
                  screen: 'FILTERSCREEN_SCREEN_CTA',
                  type: 'click',
                  optional_data: {
                    type: 'reset',
                  },
                })
              }
            }}
            key={'close-chip'}
            className={`${
              isJfsFlow && isFilterApplied ? ' bg-[#FDE7CB] border-[#FDE7CB]' : 'border-grays-40'
            } flex-row items-center self-stretch rounded  border-[1px] px-2 py-1.5`}
          >
            <Icon type="Close" color="#000" size={20} />
          </Pressable>
        )}
        {filters?.map((filter) => {
          const { isSelected, filterBadgeCount } = getFilterState(filter)
          return (
            <Pressable onPress={() => onPress(filter)} key={filter?.key}>
              <Chip
                size="large"
                type="outlined"
                style={{
                  backgroundColor:
                    isSelected && !isJfsFlow ? colorPrimary10 : isSelected && isJfsFlow ? '#FDE7CB' : '#fff',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                className={`self-start ${
                  isJfsFlow
                    ? 'rounded-3xl border-[#1C1D1F] border-opacity-10'
                    : ''
                } ${
                  isSelected && !isJfsFlow
                    ? 'border-primary-100 bg-primary-10'
                    : isSelected && isJfsFlow
                    ? 'border-[#FDE7CB]'
                    : isJfsFlow
                    ? '#E4E4E4'
                    : 'border-grays-40'
                }`}
                primaryIconType={
                  FILTER_BAR_ICONS[filter?.key as FilterBarIconsType]?.primary
                }
                primaryIconColor={colors.grays[80]}
                secondaryIconType={
                  FILTER_BAR_ICONS[filter?.key as FilterBarIconsType]?.secondary
                }
                secondaryIconColor={
                  isSelected && isBMSFlow ? colorPrimary100 : colors.grays[80]
                }
                LeadingIconComponent={
                  filterBadgeCount > 0 ? (
                    <Counter count={filterBadgeCount} />
                  ) : null
                }
              >
                {isSelected && isJfsFlow ? (
                     <SH3 className="text-[#CE912D]">
                     {filter?.displayText}
                   </SH3>
                ) : isSelected ? (
                  <SH3 className="text-primary-100 py-1">
                    {filter?.displayText}
                  </SH3>
                ) : isJfsFlow ? (
                  <SH3>{filter?.displayText}</SH3>
                ) : (
                  <B2 className={'text-grays-70 py-1'}>
                    {filter?.displayText}
                  </B2>
                )}
              </Chip>
            </Pressable>
          )
        })}
      </ScrollView>
      <SubFiltersBottomSheet
        customerId={customerId}
        categoryId={categoryId}
        ref={subFilterRef}
        initialState={{}}
        data={subFilterData}
        onSubmit={onSubFilterSubmit}
        onClear={resetSubFilters}
      />
      <DropDownFilterBottomSheet
        customerId={customerId}
        categoryId={categoryId}
        data={dropDownFilterData}
        ref={dropDownFilterBSRef}
        onChange={dropDownFilterBSRef.current?.hide}
      />
    </Stack>
  )
}

export default memo(FilterBar)
